// src/pages/ProfilePage.js
// No auth gate — About/Privacy/FAQ/Contact must be reachable with or
// without an account. Logged-out visitors get those four plus a sign-in
// prompt; logged-in users get the full personal menu.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { useSettings } from '../lib/SettingsContext';
import { callDeleteAccount, callExportUserData, callGetReferralCount } from '../lib/firebase';
import { updateUserProfile } from '../lib/db';
import { storage } from '../lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import BottomNav from '../components/layout/BottomNav';
import { PageBack } from '../components/shared/PageBack';
import toast from 'react-hot-toast';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, profile, logOut, isOrganiser, isAdmin, isTownAdmin, townAdminScope, refreshProfile } = useAuth();
  const { t, language, textSize, highContrast } = useSettings();
  const [exporting, setExporting] = useState(false);
  const [deleteStep, setDeleteStep] = useState(0); // 0 = none, 1 = first confirm, 2 = deleting
  const [referralCount, setReferralCount] = useState(null);
  const [copied, setCopied] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [bio, setBio] = useState(profile?.bio || '');
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => { setBio(profile?.bio || ''); }, [profile?.bio]);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Photo must be under 2MB'); return; }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      let photoUrl = profile?.photoUrl || null;
      if (photoFile) {
        const photoRef = ref(storage, `profile-photos/${user.uid}/${Date.now()}`);
        await uploadBytes(photoRef, photoFile);
        photoUrl = await getDownloadURL(photoRef);
      }
      await updateUserProfile(user.uid, { photoUrl, bio: bio.trim() });
      await refreshProfile();
      setPhotoFile(null);
      setPhotoPreview(null);
      toast.success('Profile updated');
    } catch (err) {
      console.error(err);
      toast.error('Could not save — please try again');
    }
    setSavingProfile(false);
  };

  useEffect(() => {
    if (!user) return;
    callGetReferralCount().then(res => setReferralCount(res.data.count)).catch(() => {});
  }, [user]);

  const referralLink = user
    ? `https://gatheryourcommunity.com/?ref=${user.uid}`
    : 'https://gatheryourcommunity.com';

  const shareReferral = async () => {
    const shareText = `Join me on Gather — the free community noticeboard for Loughrea. Everything happening locally, in one place: ${referralLink}`;
    if (navigator.share) {
      try { await navigator.share({ text: shareText }); } catch {}
    } else {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleLogout = async () => {
    await logOut();
    navigate('/');
    toast.success('Signed out');
  };

  const changeTown = () => {
    localStorage.removeItem('gather-town-id');
    navigate('/county');
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const result = await callExportUserData();
      const data = result.data;

      const XLSX = await import('xlsx');
      const wb = XLSX.utils.book_new();

      const addSheet = (name, rows) => {
        const safeRows = Array.isArray(rows) && rows.length > 0 ? rows : [{ '(none)': 'No data in this section' }];
        const ws = XLSX.utils.json_to_sheet(safeRows);
        XLSX.utils.book_append_sheet(wb, ws, name.slice(0, 31)); // Excel sheet name limit
      };

      addSheet('Profile', [data.profile || {}]);
      addSheet('Events I Posted', data.eventsPosted);
      addSheet('My Interests', data.interests);
      addSheet('Events of Interest', data.tickets);
      addSheet('My Registrations', data.registrations);
      addSheet('Following', data.follows);

      XLSX.writeFile(wb, `gather-my-data-${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success('Your data has been downloaded');
    } catch (err) {
      console.error(err);
      toast.error('Could not export your data — please try again');
    }
    setExporting(false);
  };

  const handleDeleteAccount = async () => {
    if (deleteStep === 0) { setDeleteStep(1); return; }
    setDeleteStep(2);
    try {
      await callDeleteAccount();
      toast.success('Your account has been deleted');
      navigate('/');
    } catch (err) {
      console.error(err);
      toast.error('Could not delete your account — please try again or email hello@gatheryourcommunity.com');
      setDeleteStep(0);
    }
  };

  // Settings summary for display
  const settingsSummary = [
    language !== 'en' ? language.toUpperCase() : null,
    textSize !== 'normal' ? `${textSize} text` : null,
    highContrast ? 'High contrast' : null,
  ].filter(Boolean).join(' · ');

  // Always reachable — no login required
  const publicItems = [
    { label: 'About Gather', action: () => navigate('/about') },
    { label: 'Privacy policy', action: () => navigate('/privacy') },
    { label: 'FAQ', action: () => navigate('/faq') },
    { label: 'Contact us', action: () => navigate('/contact') },
  ];

  // Personal items — only when signed in
  const personalItems = [
    isOrganiser && { label: 'Organiser dashboard', action: () => navigate('/organiser') },
    (isAdmin || isTownAdmin) && { label: 'Admin panel', action: () => navigate('/admin') },
    { label: 'Change my town', action: changeTown },
    !isOrganiser && { label: 'Become an Organiser', action: () => navigate('/become-organiser') },
    { label: 'Events of interest', action: () => navigate('/tickets') },
    { label: 'My Posts', action: () => navigate('/my-posts') },
    {
      label: `Accessibility & language${settingsSummary ? ` · ${settingsSummary}` : ''}`,
      action: () => navigate('/settings'),
    },
  ].filter(Boolean);

  const renderMenuItem = (item, i, danger = false) => (
    <button
      key={i}
      onClick={item.action}
      style={{
        width: '100%', background: 'var(--white)', border: 'none',
        borderRadius: 14, padding: '16px 18px',
        display: 'flex', alignItems: 'center', gap: 12,
        cursor: 'pointer', marginBottom: 10,
        fontSize: 15, fontWeight: 700, fontFamily: 'Nunito',
        color: danger ? 'var(--error)' : 'var(--ink)',
        boxShadow: 'var(--shadow-sm)', textAlign: 'left',
        justifyContent: 'space-between', minHeight: 54,
      }}
      aria-label={item.label}
    >
      <span>{item.label}</span>
      <span style={{ color: 'var(--border)', fontSize: 18 }} aria-hidden="true">›</span>
    </button>
  );

  return (
    <div className="page" style={{ background: 'var(--cream)', padding: '52px 20px 20px' }}>
      <PageBack fallbackPath="/" />

      <h1 style={{ fontSize: 28, fontWeight: 800, color: 'var(--ink)', marginBottom: 24, letterSpacing: '-0.3px' }}>
        {t('profile')}
      </h1>

      {user ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, background: 'var(--white)', borderRadius: 18, padding: 18, marginBottom: 16, boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ width: 56, height: 56, background: 'var(--sage)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 800, color: 'var(--sage-dark)', overflow: 'hidden', flexShrink: 0 }}
            aria-hidden="true">
            {(photoPreview || profile?.photoUrl) ? (
              <img src={photoPreview || profile.photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              profile?.displayName?.[0] || user?.email?.[0]?.toUpperCase() || 'G'
            )}
          </div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--ink)' }}>{profile?.displayName || 'Gather User'}</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 500 }}>{user?.email}</div>
            <div style={{ marginTop: 4 }}>
              <span className={`chip ${isAdmin ? 'chip-warm' : isOrganiser ? 'chip-sage' : 'chip-muted'}`} style={{ fontSize: 10 }}>
                {isAdmin ? 'Admin' : isTownAdmin ? `Town Admin (${townAdminScope || 'your town'})` : isOrganiser ? 'Organiser' : 'Member'}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ background: 'var(--white)', borderRadius: 18, padding: 20, marginBottom: 16, boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 }}>You're not signed in</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 500, marginBottom: 14 }}>
            Sign in to post events, save interests, and more. Browsing never requires an account.
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => navigate('/login')} style={{ flex: 1, background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 12, padding: '12px 14px', fontSize: 14, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
              Sign in
            </button>
            <button onClick={() => navigate('/signup')} style={{ flex: 1, background: 'var(--sage-pale)', color: 'var(--sage-dark)', border: 'none', borderRadius: 12, padding: '12px 14px', fontSize: 14, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
              Create account
            </button>
          </div>
        </div>
      )}

      {user && (
        <div style={{ background: 'var(--white)', borderRadius: 18, padding: 18, marginBottom: 16, boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 }}>Photo & bio</div>
          <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500, marginBottom: 14 }}>
            Both completely optional — helps neighbours recognise you if you post or comment.
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--sage-pale)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
              {(photoPreview || profile?.photoUrl) ? (
                <img src={photoPreview || profile.photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <span style={{ fontSize: 22 }} aria-hidden="true">🙂</span>
              )}
            </div>
            <label style={{ background: 'var(--sage-pale)', color: 'var(--sage-dark)', border: 'none', borderRadius: 10, padding: '10px 14px', fontSize: 13, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
              {photoFile ? 'Change photo' : profile?.photoUrl ? 'Change photo' : 'Upload photo'}
              <input type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: 'none' }} />
            </label>
          </div>

          <textarea
            value={bio}
            onChange={e => setBio(e.target.value)}
            placeholder="A line or two about yourself (optional)"
            maxLength={200}
            rows={3}
            style={{ width: '100%', border: '1px solid var(--border)', borderRadius: 12, padding: 12, fontSize: 14, fontFamily: 'Nunito', color: 'var(--ink)', resize: 'none', boxSizing: 'border-box', marginBottom: 12 }}
          />

          <button
            onClick={handleSaveProfile}
            disabled={savingProfile || (!photoFile && bio === (profile?.bio || ''))}
            style={{
              width: '100%', background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 12,
              padding: '12px 14px', fontSize: 14, fontWeight: 700, fontFamily: 'Nunito',
              cursor: savingProfile ? 'default' : 'pointer',
              opacity: (savingProfile || (!photoFile && bio === (profile?.bio || ''))) ? 0.5 : 1,
            }}
          >
            {savingProfile ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}

      {/* Personal items — signed in only */}
      {user && personalItems.map((item, i) => renderMenuItem(item, `p${i}`))}

      {/* Refer a friend — visible to everyone, the goal is spreading the
          word. Signed-in users get their own trackable link + count;
          signed-out visitors can still share, just without attribution. */}
      <div style={{ background: 'var(--sage-pale)', borderRadius: 16, padding: 18, marginBottom: 10 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 }}>💌 Refer a friend</div>
        <div style={{ fontSize: 13, color: 'var(--sage-dark)', fontWeight: 500, marginBottom: 12, lineHeight: 1.5 }}>
          Share Gather with someone — a neighbour, a club, another organiser.
          {user && referralCount !== null && referralCount > 0 && (
            <span> <strong>{referralCount} {referralCount === 1 ? 'person has' : 'people have'}</strong> joined through your invite so far.</span>
          )}
          {!user && (
            <span> Sign in to get your own personal link and see who joins through it.</span>
          )}
        </div>
        <button
          onClick={shareReferral}
          style={{ width: '100%', background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 12, padding: '12px 14px', fontSize: 13.5, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}
        >
          {copied ? '✓ Copied!' : '🔗 Share Gather'}
        </button>
      </div>

      {/* Public items — always visible, no account required */}
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', margin: '16px 0 10px' }}>
        Help & information
      </div>
      {publicItems.map((item, i) => renderMenuItem(item, `pub${i}`))}

      {/* Account management — signed in only */}
      {user && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', margin: '16px 0 10px' }}>
            Your data
          </div>
          {renderMenuItem({ label: exporting ? 'Preparing your data...' : 'Download my data', action: handleExportData }, 'export')}

          {deleteStep === 0 && renderMenuItem({ label: 'Delete my account', action: handleDeleteAccount }, 'delete', true)}
          {deleteStep === 1 && (
            <div style={{ background: 'var(--white)', border: '1.5px solid var(--error)', borderRadius: 14, padding: 16, marginBottom: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', marginBottom: 6 }}>Delete your account?</div>
              <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14, lineHeight: 1.5 }}>
                This permanently removes your profile and personal data. Events you've posted stay up but are no longer linked to you. This can't be undone.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setDeleteStep(0)} style={{ flex: 1, background: 'var(--sage-pale)', color: 'var(--sage-dark)', border: 'none', borderRadius: 10, padding: '10px 12px', fontSize: 13, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button onClick={handleDeleteAccount} style={{ flex: 1, background: 'var(--error)', color: 'white', border: 'none', borderRadius: 10, padding: '10px 12px', fontSize: 13, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
                  Yes, delete permanently
                </button>
              </div>
            </div>
          )}
          {deleteStep === 2 && (
            <div style={{ textAlign: 'center', padding: 16, fontSize: 13, color: 'var(--muted)', fontWeight: 600 }}>Deleting your account...</div>
          )}
        </>
      )}

      {/* Sign out */}
      {user && (
        <div style={{ marginTop: 6 }}>
          {renderMenuItem({ label: 'Sign out', action: handleLogout }, 'signout', true)}
        </div>
      )}

      {/* App version */}
      <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--muted)', fontWeight: 500, marginTop: 20 }}>
        Gather v1.0 · Everything happening locally, in one clear place
      </div>

      <PageBack position="bottom" fallbackPath="/" />
      <BottomNav />
    </div>
  );
};

export default ProfilePage;
